/**
 * Holdback Ledger — Phase 1c
 * ──────────────────────────
 * Append-only ledger for executor payout holdbacks tied to callbacks.
 *
 * Hard rules (locked in 1c):
 *   • Holdback STATE is DERIVED by folding the ledger. There is NO
 *     mutable "current state" field. See deriveState.ts.
 *   • Every entry is append-only. `reverse` is a NEW entry that
 *     negates a prior one — never an edit or delete.
 *   • Every entry carries: actorId, actorRole, reason (≥10 chars),
 *     createdAt. Forbidden keys (contractValue, PII) sanitized.
 *   • Mode-driven behavior only. Soft mode requires manual confirm
 *     (caller's responsibility); hard mode allows auto-execute. Both
 *     modes go through THIS writer. No duplicate code paths.
 *
 * Anchor: mem://architecture/network-execution-phase-1c
 */

import { getEnforcementBehavior, getNetworkEnforcementMode } from "../enforcementMode";

export type HoldbackAction = "create" | "release" | "pause" | "resume" | "reverse";

export interface HoldbackLedgerEntry {
  id: string;
  /** Logical holdback id — multiple entries share this id and are folded into state. */
  holdbackId: string;
  jobId: string;
  contractorId: string;
  action: HoldbackAction;
  /** USD amount. For `create` this is the gross hold; release/reverse can be partial. */
  amount: number;
  reason: string;
  actorId: string;
  actorRole: string;
  createdAt: string;
  /** Optional link to the callback that triggered this entry. */
  sourceCallbackId?: string;
  /** When `reverse`, the ledger entry id being negated. */
  reversesEntryId?: string;
  /** When `pause`, the dispute id holding the release. */
  pausedByDisputeId?: string;
  /** Mode at time of write — purely audit metadata. */
  enforcementModeAtWrite: "soft" | "hard";
}

const FORBIDDEN_LEDGER_KEYS = [
  "contractValue",
  "customerEmail",
  "customerPhone",
  "ssn",
] as const;

function sanitize<T extends Record<string, unknown>>(payload: T): T {
  const out = { ...payload };
  for (const key of FORBIDDEN_LEDGER_KEYS) {
    if (key in out) delete (out as Record<string, unknown>)[key];
  }
  return out;
}

function freshId(prefix: string, nowMs: number): string {
  return `${prefix}-${nowMs}-${Math.random().toString(36).slice(2, 8)}`;
}

interface AppendInputBase {
  ledger: HoldbackLedgerEntry[];
  jobId: string;
  contractorId: string;
  amount: number;
  reason: string;
  actorId: string;
  actorRole: string;
  sourceCallbackId?: string;
  nowIso?: string;
  id?: string;
}

export interface CreateHoldbackInput extends AppendInputBase {}
export interface ReleaseHoldbackInput extends AppendInputBase {
  holdbackId: string;
}
export interface PauseHoldbackInput extends AppendInputBase {
  holdbackId: string;
  disputeId: string;
}
export interface ResumeHoldbackInput extends AppendInputBase {
  holdbackId: string;
}
export interface ReverseHoldbackInput extends AppendInputBase {
  reversesEntryId: string;
}

export interface AppendResult {
  ledger: HoldbackLedgerEntry[];
  entry: HoldbackLedgerEntry | null;
  error?: string;
}

function validateBase(input: AppendInputBase): string | null {
  if (!input.actorId) return "missing_actor_id";
  if (!input.actorRole) return "missing_actor_role";
  if (!input.reason || input.reason.trim().length < 10) return "reason_too_short";
  if (!Number.isFinite(input.amount) || input.amount < 0) return "invalid_amount";
  return null;
}

function buildEntry(
  base: AppendInputBase,
  partial: Pick<HoldbackLedgerEntry, "action" | "holdbackId"> &
    Partial<Pick<HoldbackLedgerEntry, "reversesEntryId" | "pausedByDisputeId">>,
): HoldbackLedgerEntry {
  const now = base.nowIso ?? new Date().toISOString();
  const nowMs = Date.parse(now);
  const sanitizedReason = sanitize({ reason: base.reason }).reason ?? "";
  const mode = getNetworkEnforcementMode();
  return {
    id: base.id ?? freshId("hbl", nowMs),
    holdbackId: partial.holdbackId,
    jobId: base.jobId,
    contractorId: base.contractorId,
    action: partial.action,
    amount: base.amount,
    reason: sanitizedReason.trim(),
    actorId: base.actorId,
    actorRole: base.actorRole,
    createdAt: now,
    sourceCallbackId: base.sourceCallbackId,
    reversesEntryId: partial.reversesEntryId,
    pausedByDisputeId: partial.pausedByDisputeId,
    enforcementModeAtWrite: mode,
  };
}

export function createHoldback(input: CreateHoldbackInput): AppendResult {
  const err = validateBase(input);
  if (err) return { ledger: input.ledger, entry: null, error: err };
  const now = input.nowIso ?? new Date().toISOString();
  const holdbackId = freshId("hb", Date.parse(now));
  const entry = buildEntry(input, { action: "create", holdbackId });
  return { ledger: [...input.ledger, entry], entry };
}

export function releaseHoldback(input: ReleaseHoldbackInput): AppendResult {
  const err = validateBase(input);
  if (err) return { ledger: input.ledger, entry: null, error: err };
  const entry = buildEntry(input, { action: "release", holdbackId: input.holdbackId });
  return { ledger: [...input.ledger, entry], entry };
}

export function pauseHoldbackForDispute(input: PauseHoldbackInput): AppendResult {
  const err = validateBase(input);
  if (err) return { ledger: input.ledger, entry: null, error: err };
  const entry = buildEntry(input, {
    action: "pause",
    holdbackId: input.holdbackId,
    pausedByDisputeId: input.disputeId,
  });
  return { ledger: [...input.ledger, entry], entry };
}

export function resumeHoldback(input: ResumeHoldbackInput): AppendResult {
  const err = validateBase(input);
  if (err) return { ledger: input.ledger, entry: null, error: err };
  const entry = buildEntry(input, { action: "resume", holdbackId: input.holdbackId });
  return { ledger: [...input.ledger, entry], entry };
}

export function reverseHoldbackEntry(input: ReverseHoldbackInput): AppendResult {
  const err = validateBase(input);
  if (err) return { ledger: input.ledger, entry: null, error: err };
  const target = input.ledger.find((e) => e.id === input.reversesEntryId);
  if (!target) return { ledger: input.ledger, entry: null, error: "target_not_found" };
  const entry = buildEntry(input, {
    action: "reverse",
    holdbackId: target.holdbackId,
    reversesEntryId: target.id,
  });
  return { ledger: [...input.ledger, entry], entry };
}

/** Behavior switches surfaced for UI confirmation flows. */
export function getHoldbackBehavior() {
  return getEnforcementBehavior();
}
